import { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import toast from "react-hot-toast";
import {
  ArrowUpTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { extractPdfPageToFile, getPdfPageCount } from "./drawingPdfUtils";
import { uploadDocumentFiles } from "../Media/document-upload-service";

/**
 * Drawing PDF upload field with drop zone + single-page selection.
 *
 * Multi-page PDFs: user picks one page; that page is extracted client-side
 * (pdf-lib) into a single-page PDF before upload via the existing document flow.
 */
export const DrawingPdfUploadField = ({ t, title, disabled, onUploaded }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [selectedPage, setSelectedPage] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  // Guard against stale async results from getPdfPageCount
  const pageCountReqIdRef = useRef(0);
  const currentFileRef = useRef(null);
  const disabledRef = useRef(Boolean(disabled));

  useEffect(() => {
    disabledRef.current = Boolean(disabled);
  }, [disabled]);

  const reset = () => {
    // Invalidate any in-flight page count resolution
    pageCountReqIdRef.current += 1;
    currentFileRef.current = null;
    setFile(null);
    setNumPages(null);
    setSelectedPage(0);
    setLoadError(false);
    setUploading(false);
    setExtracting(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const uploadFile = async (fileToUpload) => {
    setUploading(true);
    try {
      const uploaded = await uploadDocumentFiles([
        {
          file: fileToUpload,
          title: title || fileToUpload.name,
          module: "drawing",
        },
      ]);
      const document = uploaded?.[0];
      if (!document?.id) {
        throw new Error("Upload failed");
      }
      if (disabledRef.current) {
        // Parent form is saving: don't mutate selected document mid-submit.
        reset();
        return;
      }
      toast.success(t("drawings.document.uploaded"));
      onUploaded(document);
      reset();
    } catch (err) {
      console.error("Error uploading drawing PDF:", err);
      toast.error(t("drawings.document.upload_error"));
      setUploading(false);
    }
  };

  const handleFile = async (selected) => {
    if (disabledRef.current) return;
    if (!selected) return;
    // Always clear the hidden input so selecting the same file path retriggers onChange
    if (inputRef.current) inputRef.current.value = "";
    const isPdf =
      selected.type === "application/pdf" ||
      selected.name?.toLowerCase()?.endsWith(".pdf");
    if (!isPdf) {
      toast.error(t("drawings.document.pdf_required"));
      return;
    }

    setLoadError(false);
    setFile(selected);
    currentFileRef.current = selected;
    setNumPages(null);
    setSelectedPage(0);

    try {
      // Capture a request id so late resolutions can be ignored
      const requestId = ++pageCountReqIdRef.current;
      const count = await getPdfPageCount(selected);
      // Ignore if another file was chosen or reset occurred
      if (pageCountReqIdRef.current !== requestId || currentFileRef.current !== selected) {
        return;
      }
      setNumPages(count);
      if (count <= 1) {
        // Re-check before upload in case state changed after setNumPages
        if (pageCountReqIdRef.current !== requestId || currentFileRef.current !== selected) {
          return;
        }
        await uploadFile(selected);
      }
    } catch (err) {
      console.error("Error reading PDF page count:", err);
      // Only surface error if this request is still current
      if (currentFileRef.current === selected) {
        setLoadError(true);
      }
    }
  };

  const handleUseSelectedPage = async () => {
    if (disabledRef.current || !file || uploading || extracting) return;
    setExtracting(true);
    try {
      const singlePageFile = await extractPdfPageToFile(file, selectedPage);
      setExtracting(false);
      await uploadFile(singlePageFile);
    } catch (err) {
      console.error("Error extracting PDF page:", err);
      setExtracting(false);
      toast.error(t("drawings.document.extract_error"));
    }
  };

  const busy = uploading || extracting;
  const interactionDisabled = Boolean(disabled || busy);
  const isMultiPage = numPages != null && numPages > 1;
  const isResolvingPageCount = !!file && numPages == null && !loadError;

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        onChange={(e) => {
          if (!disabledRef.current) handleFile(e.target.files?.[0]);
        }}
      />

      {!file || loadError ? (
        <div
          role="button"
          tabIndex={0}
          className={`flex min-h-[110px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
            dragOver
              ? "border-blue-400 bg-blue-50"
              : "border-gray-300 bg-gray-50 hover:border-gray-400"
          } ${interactionDisabled ? "pointer-events-none opacity-60" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (!interactionDisabled) handleFile(e.dataTransfer.files?.[0]);
          }}
          onClick={() => {
            if (!interactionDisabled) inputRef.current?.click();
          }}
          onKeyDown={(e) => {
            if (!interactionDisabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
        >
          <ArrowUpTrayIcon className="h-7 w-7 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">
            {t("drawings.document.upload_pdf")}
          </span>
          <span className="text-xs text-gray-500">
            {t("drawings.document.pdf_only_hint")}
          </span>
          {loadError && (
            <span className="text-xs font-medium text-red-600">
              {t("drawings.document.pdf_read_error")}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!interactionDisabled) inputRef.current?.click();
            }}
            disabled={interactionDisabled}
            className="mt-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-default disabled:bg-gray-400"
          >
            {t("drawings.document.choose_file")}
          </button>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <DocumentIcon className="h-5 w-5 flex-none text-gray-500" />
              <span className="min-w-0 truncate text-sm font-medium text-gray-800">
                {file.name}
              </span>
            </div>
            {!busy && !disabled && (
              <button
                type="button"
                onClick={reset}
                className="grid h-7 w-7 flex-none place-items-center rounded-md text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                title={t("Cancel")}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {isResolvingPageCount && (
            <p className="mt-3 text-sm text-gray-500">
              {t("drawings.document.checking_pages")}
            </p>
          )}

          {isMultiPage && (
            <div className="mt-3 space-y-3">
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {t("drawings.document.multi_page_warning", { count: numPages })}
              </div>

              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  disabled={selectedPage <= 0 || interactionDisabled}
                  onClick={() => setSelectedPage((p) => Math.max(0, p - 1))}
                  className="grid h-8 w-8 place-items-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:cursor-default disabled:opacity-40"
                  aria-label={t("drawings.viewer.prev_page")}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>

                <div className="overflow-hidden rounded-md border border-gray-300 bg-white shadow-sm">
                  <Document file={file} loading="" error="">
                    <Page
                      pageNumber={selectedPage + 1}
                      width={200}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </div>

                <button
                  type="button"
                  disabled={selectedPage >= numPages - 1 || interactionDisabled}
                  onClick={() =>
                    setSelectedPage((p) => Math.min(numPages - 1, p + 1))
                  }
                  className="grid h-8 w-8 place-items-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:cursor-default disabled:opacity-40"
                  aria-label={t("drawings.viewer.next_page")}
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>

              <p className="text-center text-xs font-medium text-gray-600">
                {t("drawings.observation.page")} {selectedPage + 1} / {numPages}
              </p>

              <button
                type="button"
                disabled={interactionDisabled}
                onClick={handleUseSelectedPage}
                className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-default disabled:bg-gray-400"
              >
                {extracting
                  ? t("drawings.document.extracting_page")
                  : uploading
                    ? t("drawings.document.uploading")
                    : t("drawings.document.use_this_page", {
                        page: selectedPage + 1,
                      })}
              </button>
              <p className="text-center text-[11px] text-gray-500">
                {t("drawings.document.single_page_hint")}
              </p>
            </div>
          )}

          {!isMultiPage && !isResolvingPageCount && uploading && (
            <p className="mt-3 text-sm text-gray-500">
              {t("drawings.document.uploading")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
